import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import ForumIcon from "@mui/icons-material/Forum";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { useDispatch, useSelector } from "react-redux";
import TaskChatsTab from "./TaskChatsTab";
import { toast } from "react-hot-toast";
import TasksActions from "./TasksActions";
import TasksListTable from "./TasksListTable";
import TasksTeamsOverviewTable from "./TasksTeamsOverviewTable";
import TasksGroupedListTable from "./TasksGroupedListTable";
import TaskStatusFilterBadges from "./TaskStatusFilterBadges";
import TaskTemplatesTable from "./TaskTemplatesTable";
import TaskActivityHistoryDrawer from "./TaskActivityHistoryDrawer";
import { fetchTeams } from "../../redux/Slice/Settings/settingsSlice";
import TaskTemplateCreateModal from "./TaskTemplateCreateModal";
import {
  clearSelectedTaskTemplate,
  createTaskTemplate,
  deleteTaskTemplate,
  fetchTaskTemplateById,
  fetchTaskTemplateFieldOptions,
  fetchTaskTemplateStatusCounts,
  fetchTaskTemplates,
  fetchTaskTemplatesForPicker,
  updateTaskTemplate,
  patchTaskTemplatePriority,
  patchTaskTemplateStatus,
  patchTaskTemplateType,
  setTemplatesLimit,
  setTemplatesPage,
} from "../../redux/Slice/Tasks/taskTemplatesSlice";
import {
  clearSelectedTask,
  createTask,
  deleteTask,
  fetchTaskById,
  fetchTaskFieldOptions,
  fetchTasks,
  fetchTasksGrouped,
  fetchTaskStatusCounts,
  fetchTasksTeamsOverview,
  patchTaskPriority,
  patchTaskStatus,
  patchTaskType,
  setTasksLimit,
  setTasksPage,
  updateTask,
} from "../../redux/Slice/Tasks/tasksSlice";
import { clearTaskChat, fetchTaskChatList } from "../../redux/Slice/Tasks/taskChatSlice";
import { formatTaskToast, formatTemplateToast, resolveTaskDisplayName, resolveTemplateDisplayName } from "../../utils/taskAlertMessages";
import { getFieldOptionValue } from "../../utils/taskTemplateChipStyles";
import {
  buildTeamsOverviewFromTaskRows,
  createDefaultStatusFilter,
  filterGroupedTasksBySearch,
  filterGroupedTasksByStatus,
} from "../../utils/taskStatusFilters";
import { sortTaskTableRows, sortTaskTemplateTableRows } from "../../utils/taskTableRowSort";
import { taskMainPageScrollSx } from "../../utils/taskTableScrollSx";
import { isSubscriptionBlockedError } from "../../utils/subscriptionError";
import { useSubscriptionGuard } from "../../hooks/useSubscriptionGuard";

const tasksSectionTitleSx = {
  fontWeight: 700,
  fontSize: { xs: "1.08rem", sm: "1.15rem", md: "1.2rem" },
  color: "text.primary",
  mb: 1.5,
};

/** Icon + text tab label (matches the existing Task Chats tab pattern). */
const tabLabelWithIcon = (Icon, text) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Icon sx={{ fontSize: "1rem" }} />
    {text}
  </Box>
);

const tasksOverviewSectionSx = {
  flexShrink: 0,
  mb: 2,
  mt: 2.5,
  pt: 0.5,
};

/** Task forms no longer use participants (assignees cover membership). */
const stripParticipantsFromTaskDraft = (draft) => {
  if (!draft || typeof draft !== "object") return draft;
  return {
    ...draft,
    participants: [],
    enabledSections: {
      ...(draft.enabledSections || {}),
      participants: false,
    },
  };
};

const TEMPLATE_DEADLINE_OFFSET_UNITS = new Set([
  "minutes",
  "hours",
  "days",
  "weeks",
]);

/** Computes `now + offset` for the template's relative deadline. */
const addTemplateDeadlineOffsetToDate = (base, offset) => {
  if (!offset || typeof offset !== "object") return null;
  const amount = Number(offset.amount);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const unit = TEMPLATE_DEADLINE_OFFSET_UNITS.has(String(offset.unit))
    ? String(offset.unit)
    : "days";
  const next = new Date(base.getTime());
  switch (unit) {
    case "minutes":
      next.setMinutes(next.getMinutes() + amount);
      break;
    case "hours":
      next.setHours(next.getHours() + amount);
      break;
    case "weeks":
      next.setDate(next.getDate() + amount * 7);
      break;
    case "days":
    default:
      next.setDate(next.getDate() + amount);
      break;
  }
  return next;
};

/**
 * Convert template deadline fields to task draft deadline.
 * Preferred path: relative `deadlineOffset` → compute against "now" so the
 * deadline is tied to when the task is being created, not when the template
 * was authored. Legacy absolute `deadline` is still honored for old templates.
 */
const mapTemplateDeadlineToTaskDraft = (fields) => {
  const next = { ...fields };
  const computedFromOffset = addTemplateDeadlineOffsetToDate(new Date(), next.deadlineOffset);
  if (computedFromOffset) {
    next.deadlineAt = computedFromOffset.toISOString();
  } else if (next.deadline != null && next.deadlineAt == null) {
    const d =
      next.deadline instanceof Date ? next.deadline : new Date(next.deadline);
    if (!Number.isNaN(d.getTime())) next.deadlineAt = d.toISOString();
  }
  delete next.deadlineOffset;
  delete next.deadline;
  return next;
};

/** Fields copied from template when applying to an existing task (edit mode). */
const TEMPLATE_FIELDS_ON_TASK_EDIT = [
  "description",
  "status",
  "priority",
  "templateType",
  "deadline",
  "deadlineOffset",
  "assignees",
  "files",
  "checklists",
  "recurringConfig",
  "enabledSections",
  "teamId",
  "teamIds",
];

const pickTemplateFieldsForTaskEdit = (templateFields) => {
  const picked = {};
  TEMPLATE_FIELDS_ON_TASK_EDIT.forEach((key) => {
    if (templateFields[key] !== undefined) picked[key] = templateFields[key];
  });
  return mapTemplateDeadlineToTaskDraft(picked);
};

/**
 * Template → task form draft.
 * Create: full template (owner forced in modal). Edit: keep task name/owner/id/chat; rest from template.
 * Does not call API — only updates local form state until user clicks Update.
 */
const applyTemplateDataToTaskDraft = (templateData, existingTask = null) => {
  if (!templateData || typeof templateData !== "object") return existingTask || null;

  const {
    _id: _templateId,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    createdBy: _createdBy,
    updatedBy: _updatedBy,
    __v: _v,
    ...templateFields
  } = templateData;

  const taskId = existingTask?._id ?? existingTask?.id;

  if (!taskId) {
    return stripParticipantsFromTaskDraft(
      mapTemplateDeadlineToTaskDraft({ ...templateFields })
    );
  }

  const fromTemplate = pickTemplateFieldsForTaskEdit(templateFields);

  return stripParticipantsFromTaskDraft({
    ...existingTask,
    ...fromTemplate,
    name: existingTask.name,
    owners: existingTask.owners,
    _id: taskId,
    _accessFlags: existingTask._accessFlags,
    recurringState: existingTask.recurringState,
    sourceTemplateId: existingTask.sourceTemplateId,
    createdBy: existingTask.createdBy,
    updatedBy: existingTask.updatedBy,
  });
};

const TaskDetails = () => {
  const dispatch = useDispatch();
  const { guard } = useSubscriptionGuard();
  const {
    rows: taskTemplateRows,
    loading: taskTemplatesLoading,
    deleting: deletingTemplate,
    saving: savingTemplate,
    pagination: templatesPagination,
    statusCounts: templatesStatusCounts,
    /** Cross-tab picker rows (Create-from-Template on Tasks tab, role suggestions) —
     * loaded once regardless of active tab, independent of the Templates tab's own
     * paginated/filtered `rows` above (see fetchTaskTemplatesForPicker). */
    pickerRows: templatePickerRows,
  } = useSelector((state) => state.taskTemplates);
  const {
    rows: taskRows,
    scope: tasksScope,
    grouped,
    teamsOverview,
    teamsOverviewLoading,
    loading: tasksLoading,
    deleting: deletingTask,
    saving: savingTask,
    selectedTask,
    pagination: tasksPagination,
    statusCounts: tasksStatusCounts,
  } = useSelector((state) => state.tasks);
  const settingsTeams = useSelector((state) => state.settings.teams || []);
  const taskTemplateFieldOptions = useSelector((state) => state.taskTemplates.fieldOptions);
  const taskFieldOptions = useSelector((state) => state.tasks.fieldOptions);

  const taskTemplatePermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.TaskTemplates || {}
  );
  const tasksPermissions = useSelector((state) => state.userDetails.userPermissions?.permissions?.Tasks || {});

  const roleName = useSelector((state) => state.userDetails.userDetails?.roleName || null);
  const isOwnerRole = roleName === "Owner";

  const authUser = useSelector((s) => s.auth?.user || s.userDetails?.userDetails);
  const currentUserId = String(authUser?._id || authUser?.id || "");

  const canViewTasks =
    isOwnerRole ||
    tasksPermissions.ViewAll === true ||
    tasksPermissions.ViewAssignedOnly === true ||
    tasksPermissions.View === true ||
    tasksPermissions.Edit === true;
  const canCreateTasks =
    isOwnerRole || tasksPermissions.Create === true || tasksPermissions.Edit === true;

  const canCreateTaskTemplates =
    isOwnerRole || taskTemplatePermissions.Create === true;
  const canEditAllTaskTemplates =
    isOwnerRole || taskTemplatePermissions.Edit === true;
  /** Anyone who can use Tasks may open templates tab; list is scoped by API (own vs all). */
  const canAccessTaskTemplatesTab = canViewTasks;

  const canEditTaskTemplate = (rowOrTemplate) => {
    if (isOwnerRole || canEditAllTaskTemplates) return true;
    if (!rowOrTemplate) return false;
    const creatorId = String(
      rowOrTemplate.createdById ||
        rowOrTemplate.createdBy?.id ||
        rowOrTemplate.createdBy?._id ||
        ""
    );
    return creatorId.length > 0 && creatorId === currentUserId;
  };

  const canSwitchTemplateToEdit = (template) =>
    modalMode !== "view" ? true : canEditTaskTemplate(template || editingTemplate);

  const [activeTab, setActiveTab] = useState(0);
  const [taskChatsBoxOpen, setTaskChatsBoxOpen] = useState(false);
  const taskChatsTotalUnread = useSelector((s) => s.taskChat?.chatList?.totalUnread || 0);
  const taskChatsSelectedId = useSelector((s) => s.taskChat?.chatList?.selectedTaskId || null);

  /** Lightweight fetch (limit=1) just to seed the tab badge's `totalUnread` count — the
   * full chat list (50/page, infinite scroll) only loads when the Task Chats dialog is
   * actually opened (see TaskChatsTab.jsx's own mount effect). `totalUnread` is computed
   * server-side over the full scope regardless of `limit`, so this stays accurate. */
  useEffect(() => {
    dispatch(fetchTaskChatList({ page: 1, limit: 1, search: "" }));
  }, [dispatch]);

  const [tasksSearch, setTasksSearch] = useState("");
  const [templatesSearch, setTemplatesSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("all");
  /** True only after picking a team row on Teams Tasks Overview (not toolbar team filter). */
  const [, setTeamGroupedFromOverview] = useState(false);
  const [taskStatusFilter, setTaskStatusFilter] = useState(createDefaultStatusFilter);
  const [templateStatusFilter, setTemplateStatusFilter] = useState(createDefaultStatusFilter);
  /** Tasks sub-tab: "mine" | "all" (Owner/ViewAll), "tasks" (team leader list), "overview". */
  const [taskScopeTab, setTaskScopeTab] = useState("mine");

  // Server-side search is debounced 400ms (mirrors Inbox) so the search box stays
  // instantly responsive while the network request itself is throttled. The raw,
  // undebounced values keep feeding the Grouped-view / Teams-Overview client-side
  // filters below, which must stay instant since they're untouched by this refactor.
  const [debouncedTasksSearch, setDebouncedTasksSearch] = useState("");
  const [debouncedTemplatesSearch, setDebouncedTemplatesSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTasksSearch(tasksSearch), 400);
    return () => clearTimeout(t);
  }, [tasksSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTemplatesSearch(templatesSearch), 400);
    return () => clearTimeout(t);
  }, [templatesSearch]);

  const taskStatusParam = taskStatusFilter.isAll
    ? "all"
    : Array.from(taskStatusFilter.selected)[0] || "pending";
  const templateStatusParam = templateStatusFilter.isAll
    ? "all"
    : Array.from(templateStatusFilter.selected)[0] || "pending";

  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templatesToDelete, setTemplatesToDelete] = useState([]);

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskModalMode, setTaskModalMode] = useState("create");
  const [editingTask, setEditingTask] = useState(null);
  const [tasksToDelete, setTasksToDelete] = useState([]);
  const [taskSplitLayout, setTaskSplitLayout] = useState(true);
  const [taskSourceTemplateId, setTaskSourceTemplateId] = useState(null);

  const roleOptions = useMemo(() => {
    const roles = new Set();
    templatePickerRows.forEach((row) => {
      if (row.createdBy) roles.add(row.createdBy);
      if (row.assigne) roles.add(row.assigne);
    });
    return ["All Roles", ...Array.from(roles)];
  }, [templatePickerRows]);

  const teamOptions = useMemo(() => {
    const opts = [{ value: "all", label: "All Teams" }];
    const led = (tasksScope?.ledTeamIds || []).map(String);
    const canPickAllTeams = isOwnerRole || tasksScope?.viewAll;
    (settingsTeams || []).forEach((team) => {
      const id = String(team._id || team.id);
      if (!id) return;
      if (canPickAllTeams || led.includes(id)) {
        opts.push({ value: id, label: team.teamName || team.name || "Team" });
      }
    });
    return opts;
  }, [settingsTeams, tasksScope, isOwnerRole]);

  const isTeamLeader = (tasksScope?.ledTeamIds?.length || 0) > 0;
  const canSeeTeamsOverview = isOwnerRole || tasksScope?.viewAll || isTeamLeader;
  /** Activity history drawer remains gated to Owner/ViewAll only (unchanged). */
  const canSeeTaskHistory = isOwnerRole || tasksScope?.viewAll;
  const [historyOpen, setHistoryOpen] = useState(false);

  /** Your / All sub-tabs for Owner / ViewAll (they can otherwise see everyone's tasks). */
  const showScopeSubTabs = isOwnerRole || !!tasksScope?.viewAll;

  const isOverviewTab = taskScopeTab === "overview";
  const showTaskListSection = !isOverviewTab;
  const showOverviewSection = isOverviewTab && canSeeTeamsOverview;
  const showTasksScopeTabs = showScopeSubTabs || canSeeTeamsOverview;
  const useTaskListPageScroll = showScopeSubTabs && showTaskListSection;

  const useGroupedView =
    teamFilter !== "all" &&
    (isOwnerRole ||
      tasksScope?.viewAll ||
      (tasksScope?.ledTeamIds || []).map(String).includes(teamFilter));

  /** Tasks assigned (directly) to the current user via teammember entries. */
  const isAssignedToCurrentUser = (row) => {
    if (!currentUserId) return false;
    return (row?.assignees || []).some(
      (p) => p && p.type === "teammember" && String(p.id) === currentUserId
    );
  };

  /** "My Tasks" is now enforced server-side (see fetchTasks({mine}) effect below). */
  const mineScope = showScopeSubTabs && taskScopeTab === "mine";

  // Search/status/scope/pagination are all applied server-side now (see the fetch effects
  // below) — `taskRows`/`taskTemplateRows` already arrive as exactly the current page for
  // the plain list view, so only a stable display sort remains client-side here.
  const sortedTaskRows = useMemo(() => sortTaskTableRows(taskRows), [taskRows]);
  const sortedTemplateRows = useMemo(() => sortTaskTemplateTableRows(taskTemplateRows), [taskTemplateRows]);

  const scopedGrouped = useMemo(() => {
    if (!grouped) return grouped;
    return showScopeSubTabs && taskScopeTab === "mine"
      ? {
          ...grouped,
          leaderTasks: (grouped.leaderTasks || []).filter(isAssignedToCurrentUser),
          unassignedTasks: (grouped.unassignedTasks || []).filter(isAssignedToCurrentUser),
          members: (grouped.members || []).map((bucket) => ({
            ...bucket,
            tasks: (bucket.tasks || []).filter(isAssignedToCurrentUser),
          })),
        }
      : grouped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped, showScopeSubTabs, taskScopeTab, currentUserId]);

  const filteredGrouped = useMemo(() => {
    if (!scopedGrouped) return scopedGrouped;
    return filterGroupedTasksByStatus(
      filterGroupedTasksBySearch(scopedGrouped, tasksSearch),
      taskStatusFilter
    );
  }, [scopedGrouped, tasksSearch, taskStatusFilter]);

  // Same scope/search filtering as `filteredGrouped` but WITHOUT the status
  // filter — used to feed the per-member status-breakdown stats so they stay
  // stable when the status filter pill changes (only the table rows below
  // should shrink/grow with that filter, not the summary counts).
  const statsGrouped = useMemo(() => {
    if (!scopedGrouped) return scopedGrouped;
    return filterGroupedTasksBySearch(scopedGrouped, tasksSearch);
  }, [scopedGrouped, tasksSearch]);

  const filteredTeamsOverview = useMemo(() => {
    const rows = buildTeamsOverviewFromTaskRows(teamsOverview, taskRows, settingsTeams);
    const q = tasksSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (row) =>
        String(row.teamName || "")
          .toLowerCase()
          .includes(q) ||
        String(row.leaderName || "")
          .toLowerCase()
          .includes(q)
    );
  }, [teamsOverview, taskRows, settingsTeams, tasksSearch]);

  /** Cross-tab picker data (Create-from-Template on Tasks tab, role suggestions) —
   * needed regardless of which tab is active, so fetched once here, independent of
   * the Templates tab's own (lazy) list below. */
  useEffect(() => {
    if (canAccessTaskTemplatesTab) {
      dispatch(fetchTaskTemplatesForPicker());
    }
  }, [dispatch, canAccessTaskTemplatesTab]);

  useEffect(() => {
    if (canViewTasks) {
      dispatch(fetchTeams());
      dispatch(fetchTaskFieldOptions());
    }
  }, [dispatch, canViewTasks]);

  /** Team leaders use "tasks" tab value instead of Owner/ViewAll "mine" | "all". */
  useEffect(() => {
    if (!canViewTasks || showScopeSubTabs || !canSeeTeamsOverview) return;
    setTaskScopeTab((prev) => {
      if (prev === "mine" || prev === "all") return "tasks";
      return prev;
    });
  }, [canViewTasks, showScopeSubTabs, canSeeTeamsOverview]);

  /** Task Templates tab is only opened when `activeTab === 1` — fetch its list/counts/field
   * options lazily, on first (and every) visit to that tab, not on Tasks-page mount. */
  const isTemplatesTabActive = activeTab === 1;

  // ─── Task Templates: server-side pagination + debounced search + status filter ──
  const templatesFiltersKeyRef = useRef("");
  useEffect(() => {
    if (!canAccessTaskTemplatesTab || !isTemplatesTabActive) return;
    const key = `${debouncedTemplatesSearch}|${templateStatusParam}`;
    if (templatesFiltersKeyRef.current !== "" && templatesFiltersKeyRef.current !== key) {
      dispatch(setTemplatesPage(1));
    }
    templatesFiltersKeyRef.current = key;
  }, [dispatch, canAccessTaskTemplatesTab, isTemplatesTabActive, debouncedTemplatesSearch, templateStatusParam]);

  useEffect(() => {
    if (!canAccessTaskTemplatesTab || !isTemplatesTabActive) return;
    dispatch(
      fetchTaskTemplates({
        search: debouncedTemplatesSearch,
        status: templateStatusParam,
        page: templatesPagination.page,
        limit: templatesPagination.limit,
      })
    );
  }, [
    dispatch,
    canAccessTaskTemplatesTab,
    isTemplatesTabActive,
    debouncedTemplatesSearch,
    templateStatusParam,
    templatesPagination.page,
    templatesPagination.limit,
  ]);

  useEffect(() => {
    if (!canAccessTaskTemplatesTab || !isTemplatesTabActive) return;
    dispatch(fetchTaskTemplateStatusCounts());
  }, [dispatch, canAccessTaskTemplatesTab, isTemplatesTabActive]);

  useEffect(() => {
    if (!canAccessTaskTemplatesTab || !isTemplatesTabActive) return;
    dispatch(fetchTaskTemplateFieldOptions());
  }, [dispatch, canAccessTaskTemplatesTab, isTemplatesTabActive]);

  // ─── Tasks: server-side pagination + debounced search + status/scope filter ──
  const tasksFiltersKeyRef = useRef("");
  useEffect(() => {
    const key = `${teamFilter}|${debouncedTasksSearch}|${taskStatusParam}|${mineScope}|${taskScopeTab}`;
    if (tasksFiltersKeyRef.current !== "" && tasksFiltersKeyRef.current !== key) {
      dispatch(setTasksPage(1));
    }
    tasksFiltersKeyRef.current = key;
  }, [dispatch, teamFilter, debouncedTasksSearch, taskStatusParam, mineScope, taskScopeTab]);

  /** Plain (non-grouped, non-overview) task list — the one server-paginated table. */
  useEffect(() => {
    if (!canViewTasks || isOverviewTab || useGroupedView) return;
    dispatch(
      fetchTasks({
        teamId: teamFilter === "all" ? "" : teamFilter,
        search: debouncedTasksSearch,
        status: taskStatusParam,
        mine: mineScope,
        page: tasksPagination.page,
        limit: tasksPagination.limit,
      })
    );
  }, [
    dispatch,
    canViewTasks,
    isOverviewTab,
    useGroupedView,
    teamFilter,
    debouncedTasksSearch,
    taskStatusParam,
    mineScope,
    tasksPagination.page,
    tasksPagination.limit,
  ]);

  /**
   * Teams Tasks Overview: untouched — still needs the full account/scope dataset
   * (not the 25-row default) so `buildTeamsOverviewFromTaskRows` can keep its
   * per-team counts realtime-accurate against socket-patched `taskRows`.
   */
  useEffect(() => {
    if (!canViewTasks || !isOverviewTab || !canSeeTeamsOverview) return;
    dispatch(fetchTasksTeamsOverview());
    dispatch(fetchTasks({ teamId: "", limit: 500 }));
  }, [dispatch, canViewTasks, isOverviewTab, canSeeTeamsOverview]);

  /** Grouped-by-team view: untouched, still client-filtered (see filteredGrouped/statsGrouped). */
  useEffect(() => {
    if (!canViewTasks || isOverviewTab || !useGroupedView) return;
    dispatch(fetchTasksGrouped({ teamId: teamFilter }));
  }, [dispatch, canViewTasks, isOverviewTab, useGroupedView, teamFilter]);

  /** Real DB-backed status bucket counts for the top badges — independent of the loaded page. */
  useEffect(() => {
    if (!canViewTasks || isOverviewTab) return;
    dispatch(fetchTaskStatusCounts({ teamId: teamFilter === "all" ? "" : teamFilter, mine: mineScope }));
  }, [dispatch, canViewTasks, isOverviewTab, teamFilter, mineScope]);

  const handleTeamFilterChange = (value) => {
    setTeamGroupedFromOverview(false);
    setTeamFilter(value);
  };

  const handleTaskScopeTabChange = (_, nextTab) => {
    setTaskScopeTab(nextTab);
    if (nextTab === "overview") {
      setTeamGroupedFromOverview(false);
      setTeamFilter("all");
    }
  };

  const handleSelectTeamFromOverview = (teamId) => {
    setTeamGroupedFromOverview(true);
    setTeamFilter(teamId);
    setTaskScopeTab(showScopeSubTabs ? "all" : "tasks");
  };


  useEffect(() => {
    if (!isTaskModalOpen || !editingTask?._id || !selectedTask?._id) return;
    if (String(editingTask._id) !== String(selectedTask._id)) return;

    const prevUpdated = editingTask.updatedAt
      ? new Date(editingTask.updatedAt).getTime()
      : 0;
    const nextUpdated = selectedTask.updatedAt
      ? new Date(selectedTask.updatedAt).getTime()
      : 0;
    if (nextUpdated < prevUpdated) return;

    setEditingTask((prev) => ({
      ...prev,
      ...selectedTask,
      _accessFlags: prev?._accessFlags ?? selectedTask._accessFlags,
    }));
  }, [isTaskModalOpen, editingTask?._id, editingTask?.updatedAt, selectedTask]);

  const openTemplateModal = async (mode, row = null) => {
    if (mode === "edit" && !await guard([])) return;
    try {
      setModalMode(mode);
      if (row?.id) {
        const data = await dispatch(fetchTaskTemplateById(row.id)).unwrap();
        setEditingTemplate(data);
      } else {
        setEditingTemplate(null);
        dispatch(clearSelectedTaskTemplate());
      }
      setIsTemplateModalOpen(true);
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to load task template.");
      }
    }
  };

  const closeTemplateModal = () => {
    setIsTemplateModalOpen(false);
    setEditingTemplate(null);
    setModalMode("create");
    dispatch(clearSelectedTaskTemplate());
  };

  const openTaskModal = async (mode, row = null, options = {}) => {
    if (mode === "edit" && !await guard([])) return;
    const { split = true, fromTemplateRow = null } = options;
    try {
      setTaskSplitLayout(split);
      if (fromTemplateRow?.id) {
        setTaskModalMode("create");
        const data = await dispatch(fetchTaskTemplateById(fromTemplateRow.id)).unwrap();
        setEditingTask(applyTemplateDataToTaskDraft(data));
        setTaskSourceTemplateId(String(fromTemplateRow.id));
        dispatch(clearSelectedTask());
        setIsTaskModalOpen(true);
        return;
      }
      setTaskSourceTemplateId(null);
      if (row?.id) {
        const data = await dispatch(fetchTaskById(row.id)).unwrap();
        const flags = data._accessFlags || {};
        const allowEditChrome =
          isOwnerRole || flags.canFullEdit || flags.canEditChecklists;
        let resolvedMode = mode === "edit" || mode === "view" ? mode : "view";
        if (resolvedMode === "edit") {
          if (!allowEditChrome) resolvedMode = "view";

          else if (!flags.canFullEdit && !flags.canEditChecklists) resolvedMode = "view";
        }
        setTaskModalMode(resolvedMode);


        setEditingTask(data);



      }



      else {
        setTaskModalMode(mode === "create" ? "create" : "edit");
        setEditingTask(null);
        dispatch(clearSelectedTask());

      }



      setIsTaskModalOpen(true);


    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to load task.");
      }

    }


  };



  const closeTaskModal = () => {
    const chatTaskId = editingTask?._id != null ? String(editingTask._id) : null;
    setIsTaskModalOpen(false);
    setEditingTask(null);
    setTaskModalMode("create");
    setTaskSourceTemplateId(null);
    dispatch(clearSelectedTask());
    if (chatTaskId) {
      dispatch(clearTaskChat(chatTaskId));
    }
  };

  const handleSubmitTemplate = async (payload) => {
    try {
      if (modalMode === "edit" && editingTemplate?._id) {
        const updated = await dispatch(
          updateTaskTemplate({ id: editingTemplate._id, payload })
        ).unwrap();
        toast.success(
          formatTemplateToast(
            resolveTemplateDisplayName(updated) || payload?.name,
            "Task template updated successfully."
          )
        );
      } else {
        const created = await dispatch(createTaskTemplate(payload)).unwrap();
        toast.success(
          formatTemplateToast(
            resolveTemplateDisplayName(created) || payload?.name,
            "Task template created successfully."
          )
        );
      }
      closeTemplateModal();
      reloadTaskTemplates();
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to save task template.");
      }
    }
  };

  /** Refetches the templates list at the current page/filters, plus its status counts. */
  const reloadTaskTemplates = () => {
    dispatch(
      fetchTaskTemplates({
        search: debouncedTemplatesSearch,
        status: templateStatusParam,
        page: templatesPagination.page,
        limit: templatesPagination.limit,
      })
    );
    dispatch(fetchTaskTemplateStatusCounts());
    dispatch(fetchTaskTemplatesForPicker());
  };

  const reloadTasks = () => {
    if (isOverviewTab) {
      if (canSeeTeamsOverview) {
        dispatch(fetchTasksTeamsOverview());
        dispatch(fetchTasks({ teamId: "", limit: 500 }));
      }
      return;
    }
    if (useGroupedView) {
      dispatch(fetchTasksGrouped({ teamId: teamFilter }));
    } else {
      dispatch(
        fetchTasks({
          teamId: teamFilter === "all" ? "" : teamFilter,
          search: debouncedTasksSearch,
          status: taskStatusParam,
          mine: mineScope,
          page: tasksPagination.page,
          limit: tasksPagination.limit,
        })
      );
    }
    dispatch(fetchTaskStatusCounts({ teamId: teamFilter === "all" ? "" : teamFilter, mine: mineScope }));
  };

  const handleSubmitTask = async (payload) => {
    try {
      const body = { ...payload };
      if (taskModalMode === "create" && taskSourceTemplateId) {
        body.sourceTemplateId = taskSourceTemplateId;
      }
      const taskId = editingTask?._id ?? editingTask?.id;
      if (taskModalMode === "edit" && taskId) {
        const updated = await dispatch(updateTask({ id: taskId, payload: body })).unwrap();
        setEditingTask(updated);
        reloadTasks();
        return updated;
      }
      if (taskModalMode === "edit" && !taskId) {
        toast.error("Cannot update this task. Close the form and open the task again.");
        return;
      }
      const created = await dispatch(createTask(body)).unwrap();
      toast.success(formatTaskToast(resolveTaskDisplayName(created), "Task created successfully."));
      closeTaskModal();
      reloadTasks();
      return created;
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to save task.");
      }
    }
  };

  const handleDeleteTemplates = async () => {
    if (!templatesToDelete.length) return;
    if (!await guard([])) return;
    try {
      for (const row of templatesToDelete) {
        await dispatch(deleteTaskTemplate(row.id)).unwrap();
      }
      toast.success(
        templatesToDelete.length > 1
          ? `${templatesToDelete.length} task templates deleted successfully.`
          : formatTemplateToast(
              resolveTemplateDisplayName(templatesToDelete[0]),
              "Task template deleted successfully."
            )
      );
      setTemplatesToDelete([]);
      reloadTaskTemplates();
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to delete task template.");
      }
    }
  };

  const handleDeleteTasks = async () => {
    if (!tasksToDelete.length) return;
    if (!await guard([])) return;
    try {
      for (const row of tasksToDelete) {
        await dispatch(deleteTask(row.id)).unwrap();
      }
      toast.success(
        tasksToDelete.length > 1
          ? `${tasksToDelete.length} tasks deleted successfully.`
          : formatTaskToast(resolveTaskDisplayName(tasksToDelete[0]), "Task deleted successfully.")
      );
      setTasksToDelete([]);
      reloadTasks();
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to delete task.");
      }
    }
  };

  const handleStatusChange = async (row, status) => {
    if (!status || row.status === status) return;
    if (!await guard([])) return;
    try {
      const updated = await dispatch(patchTaskStatus({ id: row.id, status })).unwrap();
      const taskName = resolveTaskDisplayName(updated) || resolveTaskDisplayName(row);
      toast.success(formatTaskToast(taskName, `Status updated to "${status}".`));
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to update status.");
      }
    }
  };

  const handlePriorityChange = async (row, priority) => {
    if (row.priority === priority) return;
    if (!await guard([])) return;
    try {
      const updated = await dispatch(patchTaskPriority({ id: row.id, priority })).unwrap();
      const taskName = resolveTaskDisplayName(updated) || resolveTaskDisplayName(row);
      toast.success(formatTaskToast(taskName, `Priority updated to "${priority}".`));
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to update priority.");
      }
    }
  };

  const handleTypeChange = async (row, templateType) => {
    if (row.templateType === templateType) return;
    if (!await guard([])) return;
    try {
      const updated = await dispatch(patchTaskType({ id: row.id, templateType })).unwrap();
      const taskName = resolveTaskDisplayName(updated) || resolveTaskDisplayName(row);
      toast.success(formatTaskToast(taskName, `Type updated to "${templateType}".`));
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to update type.");
      }
    }
  };

  const handleTemplateStatusChange = async (row, status) => {
    if (row.status === status) return;
    if (!await guard([])) return;
    try {
      const updated = await dispatch(patchTaskTemplateStatus({ id: row.id, status })).unwrap();
      const templateName = resolveTemplateDisplayName(updated) || resolveTemplateDisplayName(row);
      toast.success(formatTemplateToast(templateName, `Status updated to "${status}".`));
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to update template status.");
      }
    }
  };

  const handleTemplatePriorityChange = async (row, priority) => {
    if (row.priority === priority) return;
    if (!await guard([])) return;
    try {
      const updated = await dispatch(patchTaskTemplatePriority({ id: row.id, priority })).unwrap();
      const templateName = resolveTemplateDisplayName(updated) || resolveTemplateDisplayName(row);
      toast.success(formatTemplateToast(templateName, `Priority updated to "${priority}".`));
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to update template priority.");
      }
    }
  };

  const handleTemplateTypeChange = async (row, templateType) => {
    if (row.templateType === templateType) return;
    if (!await guard([])) return;
    try {
      const updated = await dispatch(patchTaskTemplateType({ id: row.id, templateType })).unwrap();
      const templateName = resolveTemplateDisplayName(updated) || resolveTemplateDisplayName(row);
      toast.success(formatTemplateToast(templateName, `Type updated to "${templateType}".`));
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to update template type.");
      }
    }
  };

  const handleTaskCreateNew = () => {
    if (!canCreateTasks) return;
    openTaskModal("create", null, { split: true });
  };

  const handleTaskCreateFromTemplate = (t) => {
    if (!canCreateTasks) return;
    openTaskModal("create", null, { split: true, fromTemplateRow: t });
  };

  const resolveCompletedStatusLabel = () => {
    const opts = taskFieldOptions?.statusOptions || [];
    const match = opts.find((o) => {
      const v = String(getFieldOptionValue(o) || "")
        .trim()
        .toLowerCase();
      return v === "complete" || v === "completed";
    });
    return match ? getFieldOptionValue(match) : "Complete";
  };

  const handleCompleteTask = async () => {
    const taskId = editingTask?._id != null ? String(editingTask._id) : "";
    if (!taskId) return null;
    try {
      const completedStatus = resolveCompletedStatusLabel();
      await dispatch(
        patchTaskStatus({
          id: taskId,
          status: completedStatus,
          source: "form",
        })
      ).unwrap();
      const refreshed = await dispatch(fetchTaskById(taskId)).unwrap();
      setEditingTask(refreshed);
      const taskName =
        resolveTaskDisplayName(refreshed) || resolveTaskDisplayName(editingTask);
      const resultKey = String(refreshed?.status || "")
        .trim()
        .toLowerCase();
      const isComplete = resultKey === "complete" || resultKey === "completed";
      toast.success(
        formatTaskToast(
          taskName,
          isComplete
            ? "Task marked complete."
            : "Catch-up interval completed. Task is pending for the current period."
        )
      );
      reloadTasks();
      return refreshed;
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to update status.");
      }
      throw error;
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: (theme) => theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          mb: 1.25,
          pt: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => {
            if (newValue === 2) {
              setTaskChatsBoxOpen(true);
            } else {
              setActiveTab(newValue);
            }
          }}
          variant="standard"
          sx={{
            display: "inline-flex",
            minHeight: 0,
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.045)",
            borderRadius: 2,
            p: 0.6,
            "& .MuiTabs-flexContainer": { gap: 0.6 },
            "& .MuiTabs-indicator": {
              height: "100%",
              borderRadius: 1.5,
              backgroundColor: (theme) => theme.palette.background.paper,
              boxShadow: (theme) =>
                theme.palette.mode === "dark" ? "none" : "0 1px 3px rgba(0,0,0,0.14)",
              zIndex: 0,
            },
            "& .MuiTab-root": {
              zIndex: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.9rem", sm: "0.96rem", md: "1rem" },
              minHeight: { xs: 36, sm: 38, md: 40 },
              px: { xs: 2, sm: 2.5, md: 3 },
              borderRadius: 1.5,
              color: (theme) => theme.palette.text.secondary,
            },
            "& .MuiTab-root.Mui-selected": {
              color: (theme) => theme.palette.primary.main,
            },
          }}
        >
          <Tab label={tabLabelWithIcon(AssignmentOutlinedIcon, "Tasks")} />
          <Tab label={tabLabelWithIcon(ArticleOutlinedIcon, "Task Templates")} />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                Task Chats
                {taskChatsTotalUnread > 0 && (
                  <Box
                    sx={{
                      bgcolor: "error.main",
                      color: "#fff",
                      borderRadius: "10px",
                      px: 0.9,
                      py: 0.15,
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      lineHeight: 1.4,
                      minWidth: 22,
                      textAlign: "center",
                    }}
                  >
                    {taskChatsTotalUnread > 99 ? "99+" : taskChatsTotalUnread}
                  </Box>
                )}
              </Box>
            }
          />
        </Tabs>

        {(activeTab === 0 || activeTab === 1) && (
          <TaskStatusFilterBadges
            value={activeTab === 0 ? taskStatusFilter : templateStatusFilter}
            counts={activeTab === 0 ? tasksStatusCounts : templatesStatusCounts}
            onChange={activeTab === 0 ? setTaskStatusFilter : setTemplateStatusFilter}
          />
        )}
      </Box>

      <Box sx={{ flexShrink: 0, overflow: "visible", mt: 1 }}>
        <TasksActions
          activeTab={activeTab}
          searchValue={activeTab === 0 ? tasksSearch : templatesSearch}
          onSearchChange={activeTab === 0 ? setTasksSearch : setTemplatesSearch}
          teamValue={teamFilter}
          onTeamChange={handleTeamFilterChange}
          teamOptions={teamOptions}
          onCreateClick={() => {
            if (activeTab === 1 && canCreateTaskTemplates) {
              openTemplateModal("create");
            }
          }}
          canEditTemplates={canCreateTaskTemplates}
          canEditTasks={canCreateTasks}
          taskTemplateRows={templatePickerRows}
          onTaskCreateNew={handleTaskCreateNew}
          onTaskCreateFromTemplate={handleTaskCreateFromTemplate}
          canSeeHistory={canSeeTaskHistory}
          onOpenHistory={() => setHistoryOpen(true)}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {activeTab === 0 ? (
          <>
            {showTasksScopeTabs && (
              <Box
                sx={{
                  flexShrink: 0,
                  mt: 0.5,
                  mb: 0.5,
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Tabs
                  value={taskScopeTab}
                  onChange={handleTaskScopeTabChange}
                  sx={{
                    minHeight: { xs: 40, sm: 42, md: 44 },
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: { xs: "0.9rem", sm: "0.95rem", md: "1rem" },
                      minHeight: { xs: 40, sm: 42, md: 44 },
                      py: 0.9,
                    },
                    "& .MuiTab-root.Mui-selected": {
                      color: (theme) => theme.palette.primary.main,
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: (theme) => theme.palette.primary.main,
                    },
                  }}
                >
                  {showScopeSubTabs && (
                    <Tab value="mine" label={tabLabelWithIcon(PersonOutlineIcon, "My Tasks")} />
                  )}
                  {showScopeSubTabs && (
                    <Tab value="all" label={tabLabelWithIcon(ListAltOutlinedIcon, "All Tasks")} />
                  )}
                  {!showScopeSubTabs && canSeeTeamsOverview && (
                    <Tab value="tasks" label={tabLabelWithIcon(AssignmentOutlinedIcon, "Tasks")} />
                  )}
                  {canSeeTeamsOverview && (
                    <Tab
                      value="overview"
                      label={tabLabelWithIcon(GroupsOutlinedIcon, "Teams Tasks Overview")}
                    />
                  )}
                </Tabs>
              </Box>
            )}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 0,
              pt: 1,
              ...taskMainPageScrollSx,
            }}
          >
            {showOverviewSection && (
              <Box sx={tasksOverviewSectionSx}>
                <TasksTeamsOverviewTable
                  rows={filteredTeamsOverview}
                  loading={teamsOverviewLoading}
                  onSelectTeam={handleSelectTeamFromOverview}
                  usePageScroll
                />
              </Box>
            )}
            {showTaskListSection && useGroupedView ? (
              <Box
                sx={{
                  flex: "0 0 auto",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  mt: 1,
                  pt: 0.5,
                }}
              >
                {!showScopeSubTabs && !showTasksScopeTabs && (
                  <Typography sx={tasksSectionTitleSx}>All Tasks</Typography>
                )}
                <TasksGroupedListTable
                  grouped={filteredGrouped}
                  statsGrouped={statsGrouped}
                  teamName={teamOptions.find((t) => t.value === teamFilter)?.label}
                  fieldOptions={taskFieldOptions}
                  loading={tasksLoading}
                  onView={(row) => canViewTasks && openTaskModal("edit", row, { split: true })}
                  onEdit={(row) => openTaskModal("edit", row, { split: true })}
                  onDelete={(row) => setTasksToDelete([row])}
                  onBulkDelete={(rows) => setTasksToDelete(rows)}
                  onStatusChange={handleStatusChange}
                  onPriorityChange={handlePriorityChange}
                  onTypeChange={handleTypeChange}
                  canView={canViewTasks}
                />
              </Box>
            ) : showTaskListSection ? (
              <Box
                sx={{
                  flexShrink: 0,
                  mt: 2,
                  pt: 0.5,
                }}
              >
                {!showScopeSubTabs && !showTasksScopeTabs && (
                  <Typography sx={{ ...tasksSectionTitleSx, flexShrink: 0 }}>All Tasks</Typography>
                )}
                <TasksListTable
                  rows={sortedTaskRows}
                  fieldOptions={taskFieldOptions}
                  loading={tasksLoading}
                  onView={(row) => canViewTasks && openTaskModal("edit", row, { split: true })}
                  onEdit={(row) => openTaskModal("edit", row, { split: true })}
                  onDelete={(row) => setTasksToDelete([row])}
                  onBulkDelete={(rows) => setTasksToDelete(rows)}
                  onStatusChange={handleStatusChange}
                  onPriorityChange={handlePriorityChange}
                  onTypeChange={handleTypeChange}
                  canView={canViewTasks}
                  canEdit
                  bordered
                  showBulkActionsBar
                  pagination={tasksPagination}
                  onPageChange={(page) => dispatch(setTasksPage(page))}
                  onRowsPerPageChange={(limit) => {
                    dispatch(setTasksLimit(limit));
                    dispatch(setTasksPage(1));
                  }}
                  {...(useTaskListPageScroll ? { usePageScroll: true } : {})}
                />
              </Box>
            ) : null}
          </Box>
          </>
        ) : activeTab === 1 ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              pt: 1,
              mt: 0.25,
              ...taskMainPageScrollSx,
            }}
          >
          <TaskTemplatesTable
            rows={sortedTemplateRows}
            fieldOptions={taskTemplateFieldOptions}
            loading={taskTemplatesLoading}
            onView={(row) => canAccessTaskTemplatesTab && openTemplateModal("view", row)}
            onEdit={(row) => canEditTaskTemplate(row) && openTemplateModal("edit", row)}
            onDelete={(row) => canEditTaskTemplate(row) && setTemplatesToDelete([row])}
            onBulkDelete={(rows) => setTemplatesToDelete(rows)}
            onStatusChange={handleTemplateStatusChange}
            onPriorityChange={handleTemplatePriorityChange}
            onTypeChange={handleTemplateTypeChange}
            canView={canAccessTaskTemplatesTab}
            canEditRow={canEditTaskTemplate}
            pagination={templatesPagination}
            onPageChange={(page) => dispatch(setTemplatesPage(page))}
            onRowsPerPageChange={(limit) => {
              dispatch(setTemplatesLimit(limit));
              dispatch(setTemplatesPage(1));
            }}
          />
          </Box>
        ) : null}
      </Box>

      {/* Task Chats floating box */}
      <Dialog
        open={taskChatsBoxOpen}
        onClose={() => setTaskChatsBoxOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "80vw",
            height: "92vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : undefined,
            border: (t) => t.palette.mode === "dark" ? "1px solid rgba(255,255,255,0.14)" : undefined,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.25,
            px: 2,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
            bgcolor: (t) => t.palette.mode === "dark" && taskChatsSelectedId ? "background.default" : undefined,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ForumIcon sx={{ fontSize: "1.2rem", color: "primary.main" }} />
            <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>Task Chats</Typography>
            {taskChatsTotalUnread > 0 && (
              <Box
                sx={{
                  bgcolor: "error.main",
                  color: "#fff",
                  borderRadius: "10px",
                  px: 0.9,
                  py: 0.15,
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  lineHeight: 1.4,
                  minWidth: 22,
                  textAlign: "center",
                }}
              >
                {taskChatsTotalUnread > 99 ? "99+" : taskChatsTotalUnread}
              </Box>
            )}
          </Box>
          <IconButton size="small" onClick={() => setTaskChatsBoxOpen(false)}>
            <CloseIcon sx={{ fontSize: "1.1rem" }} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            flex: 1,
            minHeight: 0,
            p: 0,
            display: "flex",
            overflow: "hidden",
            "&.MuiDialogContent-root": { p: 0 },
          }}
        >
          <TaskChatsTab
            onViewTask={(taskId) => {
              setTaskChatsBoxOpen(false);
              openTaskModal("edit", { id: taskId }, { split: true });
            }}
          />
        </DialogContent>
      </Dialog>

      <TaskTemplateCreateModal
        open={isTemplateModalOpen}
        onClose={closeTemplateModal}
        roleOptions={roleOptions}
        mode={modalMode}
        initialData={editingTemplate}
        onSubmit={handleSubmitTemplate}
        onSwitchToEdit={() => setModalMode("edit")}
        submitting={savingTemplate}
        canSwitchToEdit={canSwitchTemplateToEdit(editingTemplate)}
        canManageFieldOptions={isOwnerRole}
        entityType="template"
      />

      <TaskActivityHistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />

      <TaskTemplateCreateModal
        open={isTaskModalOpen}
        onClose={closeTaskModal}
        roleOptions={roleOptions}
        mode={taskModalMode}
        initialData={editingTask}
        onSubmit={handleSubmitTask}
        onSwitchToEdit={() => setTaskModalMode("edit")}
        submitting={savingTask}
        canSwitchToEdit={
          !editingTask?._accessFlags ||
          editingTask._accessFlags.canFullEdit ||
          editingTask._accessFlags.canEditChecklists
        }
        entityType="task"
        taskAccessFlags={editingTask?._accessFlags || null}
        onCompleteTask={handleCompleteTask}
        splitWithChat={taskSplitLayout}
        showTemplatesPicker={canCreateTasks && canAccessTaskTemplatesTab}
        canManageFieldOptions={isOwnerRole || editingTask?._accessFlags?.canFullEdit}
        templatePickerRows={templatePickerRows}
        onApplyTemplateFromPicker={async (templateId) => {
          try {
            const data = await dispatch(fetchTaskTemplateById(templateId)).unwrap();
            // Local form prefill only — keeps task _id (chat) until user clicks Update/Create.
            setEditingTask((prev) => applyTemplateDataToTaskDraft(data, prev));
            if (taskModalMode === "create") {
              setTaskSourceTemplateId(String(templateId));
            }
          } catch (e) {
            toast.error(e || "Failed to load template.");
          }
        }}
      />

      <Dialog open={templatesToDelete.length > 0} onClose={() => setTemplatesToDelete([])}>
        <DialogTitle>{templatesToDelete.length > 1 ? "Delete Task Templates" : "Delete Task Template"}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary" }}>
            {templatesToDelete.length > 1
              ? `${templatesToDelete.length} task templates will be permanently deleted. This action cannot be undone.`
              : "This task template will be permanently deleted. This action cannot be undone."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplatesToDelete([])} disabled={deletingTemplate}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteTemplates} disabled={deletingTemplate}>
            {deletingTemplate ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tasksToDelete.length > 0} onClose={() => setTasksToDelete([])}>
        <DialogTitle>{tasksToDelete.length > 1 ? "Delete Tasks" : "Delete Task"}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary" }}>
            {tasksToDelete.length > 1
              ? `${tasksToDelete.length} tasks will be permanently deleted. This action cannot be undone.`
              : "This task will be permanently deleted. This action cannot be undone."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTasksToDelete([])} disabled={deletingTask}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteTasks} disabled={deletingTask}>
            {deletingTask ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskDetails;
